import React, { Component } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames/bind';

import Tooltip from '../tooltip/Tooltip';
import WithInfoTooltip from '../with_info_tooltip/WithInfoTooltip';
import styles from './TextArea.module.sass';

const cx = classNames.bind(styles);

class TextArea extends Component {
  constructor(props) {
    super(props);

    this.state = {
      focused: false,
      active: false,
    };
  }

  onChange = (event) => {
    const { onChange } = this.props;
    if (onChange) {
      onChange(event);
    }
  };

  onFocus = (event) => {
    const { onFocus } = this.props;
    this.setState({ focused: true });
    if (onFocus) {
      onFocus(event);
    }
  };

  onBlur = (event) => {
    const { onBlur } = this.props;
    this.setState({ focused: false });
    if (onBlur) {
      onBlur(event);
    }
  };

  onMouseDown = (event) => {
    const { onMouseDown } = this.props;
    this.setState({ active: true });
    if (onMouseDown) {
      onMouseDown(event);
    }
  };

  onMouseUp = (event) => {
    const { onMouseUp } = this.props;
    this.setState({ active: false });
    if (onMouseUp) {
      onMouseUp(event);
    }
  };

  iconOnClickHandler = (event) => {
    const { iconOnClickHandler, toggleHint } = this.props;
    toggleHint();

    if (iconOnClickHandler) {
      iconOnClickHandler(event);
    }
  };

  render() {
    const {
      className,
      errorTooltipClassName,
      infoTooltipClassName,
      label,
      type = 'text',
      id,
      tabIndex = 0,
      name,
      value,
      placeholder,
      description,
      disabled,
      error,
      hint,
      icon,
      hintShown,
      iconRef,
      tooltipRef,
      maxLength,
    } = this.props;
    const {
      focused,
      active,
    } = this.state;
    const inputCx = {
      disabled,
      focused,
      active,
      error,
    };

    return (
      <div className={cx(styles.textAreaContainer, className)}>
        { label }
        <label htmlFor={id} className={cx(styles.textArea, inputCx)}>
          { error
            && focused
            && (
              <Tooltip className={cx(errorTooltipClassName, styles.tooltipMargin, { descriptionTooltipMargin: description })} type="error">
                <div>{ error }</div>
              </Tooltip>
            )}
          {
            hintShown
            && hint
            && (
              <Tooltip className={cx(infoTooltipClassName, styles.tooltipMargin, { descriptionTooltipMargin: description })} type="info" ref={tooltipRef}>
                <div>{ hint }</div>
              </Tooltip>
            )
          }
          <textarea
            tabIndex={tabIndex}
            type={type}
            id={id}
            maxLength={maxLength}
            name={name}
            value={value}
            placeholder={placeholder}
            onChange={this.onChange}
            onFocus={this.onFocus}
            onBlur={this.onBlur}
            onMouseDown={this.onMouseDown}
            onMouseUp={this.onMouseUp}
            disabled={disabled}
          />
          {
            icon
            && (
              <button tabIndex="-1" type="button" ref={iconRef} onClick={this.iconOnClickHandler}>{ icon }</button>
            )
          }
        </label>
        {
          description && (<p className={styles.description}>{ description }</p>)
        }
      </div>
    );
  }
}

TextArea.propTypes = {
  /** Any CSS classes to apply to component. */
  className: PropTypes.string,
  /** Any CSS classes to apply to component error tooltip. */
  errorTooltipClassName: PropTypes.string,
  /** Any CSS classes to apply to component info tooltip. */
  infoTooltipClassName: PropTypes.string,
  /** Label for textarea. */
  label: PropTypes.string,
  /** Specifies the type of textarea (default is text). */
  type: PropTypes.string,
  /** Id for textarea. */
  id: PropTypes.string,
  /** Tab index for component's textarea tag. */
  tabIndex: PropTypes.number,
  /** Name for textarea. */
  name: PropTypes.string,
  /** Default value for textarea. */
  value: PropTypes.string,
  /** Placeholder for textarea. */
  placeholder: PropTypes.string,
  /** Description for textarea */
  description: PropTypes.string,
  /** Error text to show in tooltip above textarea. */
  error: PropTypes.string,
  /** Hint text to show in tooltip above textarea. */
  hint: PropTypes.node,
  /** Boolean indicationg whether to show hint tooltip. */
  hintShown: PropTypes.bool,
  /** Handler to toggle hint. */
  toggleHint: PropTypes.func,
  /** Clickable icon to render within textarea container, used to toggle hint. */
  icon: PropTypes.node,
  /** Boolean indicating whether textarea should render as disabled. */
  disabled: PropTypes.bool,
  /** onChange handler. */
  onChange: PropTypes.func,
  /** onFocus handler. */
  onFocus: PropTypes.func,
  /** onBlur handler. */
  onBlur: PropTypes.func,
  /** onMouseDown handler. */
  onMouseDown: PropTypes.func,
  /** onMouseUp handler. */
  onMouseUp: PropTypes.func,
  /** Handler for icon. */
  iconOnClickHandler: PropTypes.func,
  /** Ref for icon. */
  iconRef: PropTypes.shape({}),
  /** Ref for icon. */
  tooltipRef: PropTypes.shape({}),
  /** max length for textarea. */
  maxLength: PropTypes.number,
};

/** Wrapper for textarea component to support hiding info tooltip when clicking outside. */
function TextAreaWithInfoTooltip(props) {
  return (
    <WithInfoTooltip>
      <TextArea {...props} />
    </WithInfoTooltip>
  );
}

TextAreaWithInfoTooltip.propTypes = TextArea.propTypes;

export default TextAreaWithInfoTooltip;
